import { useCallback, useReducer, useRef, useState } from "react";
import type { WorkbookHistoryEntry } from "./workbook-history-types";

// The two-stack undo/redo model for the workbook editor: pushing a new edit
// clears any redo history (Excel/Google-Sheets semantics), undo moves the top
// entry from undo → redo, and redo moves it back. The reducer is PURE — it only
// shuffles entries between the stacks; replaying an entry's ops against the API
// (and marking `isApplying` while that round-trip is in flight) is the caller's
// job (see the viewer's undo/redo handlers).

export interface WorkbookHistoryState {
  undoStack: WorkbookHistoryEntry[];
  redoStack: WorkbookHistoryEntry[];
}

export const initialWorkbookHistoryState: WorkbookHistoryState = {
  undoStack: [],
  redoStack: [],
};

export type WorkbookHistoryAction =
  // A new edit was committed: record it and drop the redo history.
  | { type: "push"; entry: WorkbookHistoryEntry }
  // Move the most recent edit from the undo stack onto the redo stack.
  | { type: "undo" }
  // Move the most recently undone edit back onto the undo stack.
  | { type: "redo" }
  // Clear both stacks (e.g. when switching runs).
  | { type: "reset" };

export const workbookHistoryReducer = (
  state: WorkbookHistoryState,
  action: WorkbookHistoryAction,
): WorkbookHistoryState => {
  switch (action.type) {
    case "push":
      return { undoStack: [...state.undoStack, action.entry], redoStack: [] };
    case "undo": {
      if (state.undoStack.length === 0) return state;

      const entry = state.undoStack[state.undoStack.length - 1];

      return {
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, entry],
      };
    }
    case "redo": {
      if (state.redoStack.length === 0) return state;

      const entry = state.redoStack[state.redoStack.length - 1];

      return {
        undoStack: [...state.undoStack, entry],
        redoStack: state.redoStack.slice(0, -1),
      };
    }
    case "reset":
      return initialWorkbookHistoryState;
    default:
      return state;
  }
};

export interface UseWorkbookHistoryResult {
  canUndo: boolean;
  canRedo: boolean;
  // The label of the next entry that undo / redo would act on, for the
  // menu/tooltip copy ("Undo edit cell B4"). Null when the stack is empty.
  undoLabel: string | null;
  redoLabel: string | null;
  // Record a freshly committed edit (clears the redo history).
  push: (entry: WorkbookHistoryEntry) => void;
  // Pop the top undo entry, move it to the redo stack, and return it so the
  // caller can replay its `inverse` ops. Returns null when there's nothing to
  // undo.
  beginUndo: () => WorkbookHistoryEntry | null;
  // Pop the top redo entry, move it back to the undo stack, and return it so the
  // caller can replay its `forward` ops. Returns null when there's nothing to
  // redo.
  beginRedo: () => WorkbookHistoryEntry | null;
  // Clear both stacks.
  reset: () => void;
  // True while the caller is replaying an entry against the API. The reducer
  // never touches this; the caller flips it via `setApplying` so it can disable
  // the undo/redo controls (and its own edit handlers) during a replay.
  isApplying: boolean;
  setApplying: (applying: boolean) => void;
}

// Manages the undo/redo stacks for the workbook editor. The hook owns the
// stacks and the `isApplying` guard, but stays side-effect free: `beginUndo` /
// `beginRedo` hand the entry back to the caller to replay, they don't call the
// API themselves.
export const useWorkbookHistory = (): UseWorkbookHistoryResult => {
  const [state, dispatch] = useReducer(
    workbookHistoryReducer,
    initialWorkbookHistoryState,
  );
  const [isApplying, setApplying] = useState(false);

  // Mirror each action synchronously. Batched edit promises can settle in one
  // tick, so every push must build on the prior push before React re-renders.
  const stateRef = useRef(initialWorkbookHistoryState);

  const push = useCallback((entry: WorkbookHistoryEntry) => {
    const action = { type: "push", entry } as const;
    stateRef.current = workbookHistoryReducer(stateRef.current, action);
    dispatch(action);
  }, []);

  const beginUndo = useCallback((): WorkbookHistoryEntry | null => {
    const { undoStack } = stateRef.current;
    const entry = undoStack[undoStack.length - 1] ?? null;

    if (!entry) return null;

    const action = { type: "undo" } as const;
    stateRef.current = workbookHistoryReducer(stateRef.current, action);
    dispatch(action);

    return entry;
  }, []);

  const beginRedo = useCallback((): WorkbookHistoryEntry | null => {
    const { redoStack } = stateRef.current;
    const entry = redoStack[redoStack.length - 1] ?? null;

    if (!entry) return null;

    const action = { type: "redo" } as const;
    stateRef.current = workbookHistoryReducer(stateRef.current, action);
    dispatch(action);

    return entry;
  }, []);

  const reset = useCallback(() => {
    const action = { type: "reset" } as const;
    stateRef.current = workbookHistoryReducer(stateRef.current, action);
    dispatch(action);
  }, []);

  const undoTop = state.undoStack[state.undoStack.length - 1];
  const redoTop = state.redoStack[state.redoStack.length - 1];

  return {
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
    undoLabel: undoTop?.label ?? null,
    redoLabel: redoTop?.label ?? null,
    push,
    beginUndo,
    beginRedo,
    reset,
    isApplying,
    setApplying,
  };
};
